import { Injectable } from '@angular/core';
import { UserService } from '../../../../../chat-common/src/lib/services/user.service';
import { addDoc, collection, collectionData, CollectionReference, DocumentData, Firestore, getDocs, limit, onSnapshot, orderBy, query, Unsubscribe } from '@angular/fire/firestore';
import { Message } from '../../../../../chat-common/src/lib/models/message';
import { ChatMessage } from '../models/chat-message';
import { BehaviorSubject, firstValueFrom, Observable, take } from 'rxjs';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class GptService {

  private pathToChat: string = '';
  private unsubscribe: Unsubscribe | null = null;
  private chatMessagesSubject: BehaviorSubject<ChatMessage[]> = new BehaviorSubject<ChatMessage[]>([]);
  private chatOnboardingSubject: BehaviorSubject<ChatMessage[]> = new BehaviorSubject<ChatMessage[]>([]);
  public chatMessages$: Observable<ChatMessage[]> = this.chatMessagesSubject.asObservable();
  public chatOnboardingMessages$: Observable<ChatMessage[]> = this.chatOnboardingSubject.asObservable();

  constructor(private userService: UserService,
    private firestore: Firestore,
  ) {
  }

  getChat() {
    this.observeChat();
  }

  getOnboardingChat() {
    this.observeOnboardingChat();
  }

  async getTotalDaysAvailable(): Promise<number> {
    const chatCollection = collection(
      this.firestore,
      'v0',
      'data',
      'user',
      this.userService.user.id,
      'bellaChat',
      'chatHistory',
      'chatHealthAssistant'
    );


    const q = query(chatCollection, orderBy('date', 'asc'), limit(1));
    const snap = await getDocs(q);

    if (snap.empty) {
      return 0;
    }

    const firstDoc = snap.docs[0].data() as any;

    if (!firstDoc?.date) return 0;

    const firstDay = moment(firstDoc.date.toDate()).startOf('day');
    const today = moment().startOf('day');

    return today.diff(firstDay, 'days');
  }

  async hasUserInterrupted(): Promise<boolean> {
    const chatCollection = collection(
      this.firestore,
      'v0',
      'data',
      'user',
      this.userService.user.id,
      'bellaChat',
      'chatHistory',
      'chatHealthAssistant'
    );

    // Limit(100) 
    const q = query(chatCollection, orderBy('date', 'desc'), limit(100));
    const snap = await getDocs(q);

    if (snap.empty) return false;

    for (const doc of snap.docs) {
      const msg = doc.data() as any;
      if (
        msg?.source === 'user' &&
        msg?.message &&
        msg.message.trim() !== '' &&
        !msg.message.startsWith('#chatresponse')
      ) {
        return true;
      }
    }

    return false;
  }

  initChat() {
    this.sendMessage(new ChatMessage(''))
      .then(r => console.log('Chat init complete'))
  }

  initOnboardingChat() {
    this.sendMessage(new ChatMessage(''))
      .then(r => console.log('Chat init complete'))
  }

  sendMockMessage() {
    this.sendMessage(new ChatMessage(''))
  }

  sendCannedResponseMessage(data: string) {
    this.sendMessage(new ChatMessage(''))
  }

  sendMessage(message: ChatMessage) {
    return Promise.all([addDoc(
      collection(this.firestore,
        'v0',
        'data',
        'user',
        this.userService.user.id,
        'bellaChat',
        'chatHistory',
        'chatHealthAssistant'),
      Object.assign({}, message)
    ), Promise.resolve(message)]);
  }

  sendAssistantMessage(message: ChatMessage) {
    const colRef = collection(
      this.firestore,
      'v0', 'data', 'user', this.userService.user.id,
      'bellaChat', 'chatHistory', 'chatHealthAssistant'
    )

    const payload = ChatMessage.toFirestoreMessage(message);

    return Promise.all([
      addDoc(colRef, payload),
      Promise.resolve(message),
    ]);
  }

  sendOnboardingMessage(message: ChatMessage) {
    const colRef = collection(
      this.firestore,
      'v0', 'data', 'user', this.userService.user.id,
      'bellaChat', 'chatHistory', 'chatHealthAssistantOnboarding'
    )

    const payload = ChatMessage.toFirestoreMessage(message);

    return Promise.all([
      addDoc(colRef, payload),
      Promise.resolve(message),
    ]);
  }

  private observeChat() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }

    const chatCollection = collection(
      this.firestore,
      'v0',
      'data',
      'user',
      this.userService.user.id,
      'bellaChat',
      'chatHistory',
      'chatHealthAssistant'
    );

    const q = query(chatCollection, orderBy('date', 'asc'));

    this.unsubscribe = onSnapshot(q, (snapshot) => {
      const messages: ChatMessage[] = [];
      const lisOfChanges = snapshot.docChanges();
      if (Array.isArray(lisOfChanges) && lisOfChanges.length > 0) {
        lisOfChanges.forEach((change) => {
          if (change.type === 'added') {
            const messageData = change.doc.data();
            const message: ChatMessage = {
              id: change.doc.id,
              userId: '',
              message: messageData['message'],
              source: messageData['source'],
              date: messageData['date'].toDate(), // Convert Timestamp to Date
              application: messageData['application'],
              end: messageData['end'],
              chatVersion: '2.0',
              answer: messageData['answer'],
            };
            messages.push(message);
          }
        });
        this.chatMessagesSubject.next([...this.chatMessagesSubject.value, ...messages]);
      }
      else {
        //this.initChat();
      }

    });
  }

  async getOnboardingMessagesOnce(): Promise<any[]> {
    const chatCollection: CollectionReference<DocumentData> = collection(
      this.firestore,
      'v0',
      'data',
      'user',
      this.userService.user.id,
      'bellaChat',
      'chatHistory',
      'chatHealthAssistantOnboarding'
    );

    const messages = await firstValueFrom(
      collectionData(chatCollection, { idField: 'id' }).pipe(take(1))
    );

    return messages;
  }

  async getMessagesOnce(): Promise<any[]> {
    const chatCollection: CollectionReference<DocumentData> = collection(
      this.firestore,
      'v0',
      'data',
      'user',
      this.userService.user.id,
      'bellaChat',
      'chatHistory',
      'chatHealthAssistant'
    );

    const messages = await firstValueFrom(
      collectionData(chatCollection, { idField: 'id' }).pipe(take(1))
    );

    return messages;
  }

  private observeOnboardingChat() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }

    const chatCollection = collection(
      this.firestore,
      'v0',
      'data',
      'user',
      this.userService.user.id,
      'bellaChat',
      'chatHistory',
      'chatHealthAssistantOnboarding'
    );

    const q = query(chatCollection, orderBy('date', 'asc'));

    this.unsubscribe = onSnapshot(q, (snapshot) => {
      const messages: ChatMessage[] = [];
      const lisOfChanges = snapshot.docChanges();
      if (Array.isArray(lisOfChanges) && lisOfChanges.length > 0) {
        lisOfChanges.forEach((change) => {
          if (change.type === 'added') {
            const messageData = change.doc.data();
            const message: ChatMessage = {
              id: change.doc.id,
              userId: '',
              message: messageData['message'],
              source: messageData['source'],
              date: messageData['date'].toDate(), // Convert Timestamp to Date
              application: messageData['application'],
              end: messageData['end'],
              chatVersion: '2.0',
              answer: messageData['answer'],
            };
            messages.push(message);
          }
        });
        this.chatOnboardingSubject.next([...this.chatOnboardingSubject.value, ...messages]);
      }


    });
  }
}
